<template>
  <div
    class="resident-notifications__results"
    data-resident-notifications-results
    :class="{ 'is-loading': loading }"
    :aria-busy="loading ? 'true' : undefined"
  >
    <div v-if="!loading && !items.length && !searchQuery.trim()" class="resident-notifications__empty">
      <h3 class="resident-notifications__empty-title">Нет уведомлений</h3>
      <p class="resident-notifications__empty-text">Они появятся, загляните позже :)</p>
    </div>

    <div v-else-if="items.length" class="resident-notifications__list">
      <div
        v-for="item in items"
        :key="item.id"
        class="notification-item"
        :class="{
          'notification-item--new': item.isNew,
        }"
        role="button"
        tabindex="0"
        data-notification-open-drawer
        :data-notification-id="item.id"
        :data-notification-title="item.title"
        :data-notification-date="item.date"
        :data-notification-description="item.description"
      >
        <div class="notification-item__left-container">
          <span class="notification-item__left-container--title">{{ item.title }}</span>
          <span class="notification-item__left-container--description">{{ item.description }}</span>
        </div>

        <div class="notification-item__right-container">
          <span class="notification-item__right-container--date">{{ item.date }}</span>

          <div class="notification-item__right-container--status-container">
            <span class="notification-item__right-container--status-dot"></span>
            <span class="notification-item__right-container--status">{{ getNotificationStatusLabel(item.statusCode) }}</span>
          </div>
        </div>
      </div>
    </div>

    <Pagination
      v-if="nav.totalCount > nav.pageSize"
      v-model="currentPage"
      class="resident-notifications__pagination"
      :total-count="nav.totalCount"
      :page-size="nav.pageSize"
      @change="handlePageChange"
    />
  </div>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { formatMatchCountSummary } from '@/js/utils/search-highlight';
import {
  applyServerSearchResults,
  initSearchInput,
  querySearchInput,
  type SearchHighlightConfig,
} from '@/js/utils/search-input';
import { updateNotificationsSidebarBadge } from '@/js/utils/notifications-sidebar-badge';
import Pagination from '../Pagination.vue';
import {
  fetchNotificationList,
  NOTIFICATIONS_PAGE_PARAM,
  RESIDENT_LK_NOTIFICATIONS_TAB_ID,
} from './notification-api';
import {
  getNotificationStatusLabel,
  mapNotificationToCard,
  type NotificationCardItem,
} from './map-notification';

const CHANGED_EVENT = 'residentNotifications:changed';
const SEARCH_INPUT_ID = 'residentNotificationsSearch';
const SEARCH_MIN_LENGTH = 3;

const NOTIFICATIONS_SEARCH_HIGHLIGHT: SearchHighlightConfig = {
  cardSelector: '.notification-item',
  fields: [
    { selector: '.notification-item__left-container--title' },
    { selector: '.notification-item__left-container--description' },
  ],
};

const loading = ref(false);
const items = ref<NotificationCardItem[]>([]);
const searchQuery = ref('');
const currentPage = ref(1);
const nav = reactive({
  totalCount: 0,
  pageSize: 20,
  currentPage: 1,
  pageCount: 1,
});

let cleanupSearchInput: (() => void) | null = null;
let cleanupNotificationsReload: (() => void) | null = null;
let searchDebounceTimer: ReturnType<typeof setTimeout> | undefined;
let lastSubmittedSearch = '';
let searchReady = false;

onMounted(() => {
  syncSearchFromUrl();
  bindSearchInput();
  bindNotificationsReload();
  void loadNotifications();
  queueMicrotask(() => {
    searchReady = true;
    lastSubmittedSearch = searchQuery.value;
  });
});

onBeforeUnmount(() => {
  cleanupSearchInput?.();
  cleanupNotificationsReload?.();
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
  }
});

function syncSearchFromUrl(): void {
  const params = new URLSearchParams(window.location.search);
  searchQuery.value = params.get('q') ?? '';
  currentPage.value = parsePageFromUrl(params);
}

function parsePageFromUrl(params: URLSearchParams): number {
  const pageParam = params.get(NOTIFICATIONS_PAGE_PARAM) ?? params.get('notifications-page');
  const match = pageParam?.match(/^page-(\d+)$/);
  return match ? Math.max(1, Number(match[1])) : 1;
}

function shouldSubmitSearch(value: string): boolean {
  const trimmed = value.trim();

  if (!trimmed) {
    return true;
  }

  if (trimmed.length >= SEARCH_MIN_LENGTH) {
    return true;
  }

  return lastSubmittedSearch.trim().length >= SEARCH_MIN_LENGTH;
}

function syncFiltersVisibility(): void {
  const root = document.querySelector<HTMLElement>('[data-resident-notifications]');
  if (!root) return;

  const isCompletelyEmpty =
    !loading.value && nav.totalCount === 0 && !searchQuery.value.trim();

  root.classList.toggle('resident-notifications--empty', isCompletelyEmpty);
}

function updateSearchSummary(): void {
  const summary = querySearchInput(document, SEARCH_INPUT_ID)?.summary;
  if (!summary) return;

  const query = searchQuery.value.trim();
  if (!query || query.length < SEARCH_MIN_LENGTH) {
    summary.hidden = true;
    summary.innerHTML = '';
    return;
  }

  summary.hidden = false;
  summary.innerHTML = formatMatchCountSummary(nav.totalCount, query);
}

async function syncSearchHighlight(): Promise<void> {
  await nextTick();

  const query = searchQuery.value.trim();
  if (!query || query.length < SEARCH_MIN_LENGTH) {
    applyServerSearchResults(document, '', {
      inputId: SEARCH_INPUT_ID,
      highlight: NOTIFICATIONS_SEARCH_HIGHLIGHT,
    });
    updateSearchSummary();
    return;
  }

  applyServerSearchResults(document, query, {
    inputId: SEARCH_INPUT_ID,
    highlight: NOTIFICATIONS_SEARCH_HIGHLIGHT,
  });
  updateSearchSummary();
}

function bindSearchInput(): void {
  const elements = initSearchInput(document, {
    inputId: SEARCH_INPUT_ID,
    mode: 'server',
    urlParam: 'q',
  });
  if (!elements) {
    return;
  }

  elements.input.value = searchQuery.value;

  const scheduleSearch = (): void => {
    if (!searchReady) {
      return;
    }

    searchQuery.value = elements.input.value;

    if (!shouldSubmitSearch(searchQuery.value)) {
      if (searchQuery.value.trim().length < SEARCH_MIN_LENGTH) {
        void syncSearchHighlight();
      }
      return;
    }

    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }

    searchDebounceTimer = setTimeout(() => {
      lastSubmittedSearch = searchQuery.value;
      currentPage.value = 1;
      syncUrlToBrowser();
      void loadNotifications();
    }, 400);
  };

  const submitSearchImmediately = (): void => {
    if (!searchReady) {
      return;
    }

    searchQuery.value = elements.input.value;
    lastSubmittedSearch = searchQuery.value;
    currentPage.value = 1;
    syncUrlToBrowser();
    void loadNotifications();
  };

  const handleClear = (): void => {
    queueMicrotask(submitSearchImmediately);
  };

  elements.input.addEventListener('input', scheduleSearch);
  elements.clearBtn.addEventListener('click', handleClear);

  cleanupSearchInput = () => {
    elements.input.removeEventListener('input', scheduleSearch);
    elements.clearBtn.removeEventListener('click', handleClear);
    cleanupSearchInput = null;
  };
}

function bindNotificationsReload(): void {
  const reloadNotifications = (): void => {
    void loadNotifications();
  };

  window.addEventListener(CHANGED_EVENT, reloadNotifications);
  cleanupNotificationsReload = () => {
    window.removeEventListener(CHANGED_EVENT, reloadNotifications);
    cleanupNotificationsReload = null;
  };
}

function syncUrlToBrowser(): void {
  const url = new URL(window.location.pathname, window.location.origin);
  url.searchParams.set('tab', String(RESIDENT_LK_NOTIFICATIONS_TAB_ID));

  const query = searchQuery.value.trim();
  if (query) {
    url.searchParams.set('q', query);
  }

  if (currentPage.value > 1) {
    url.searchParams.set(NOTIFICATIONS_PAGE_PARAM, `page-${currentPage.value}`);
  }

  window.history.replaceState({ tab: RESIDENT_LK_NOTIFICATIONS_TAB_ID }, '', url.toString());
}

async function loadNotifications(): Promise<void> {
  loading.value = true;

  try {
    const data = await fetchNotificationList({
      q: searchQuery.value,
      page: currentPage.value,
    });

    items.value = data.messages.map(mapNotificationToCard);
    nav.totalCount = data.nav.totalCount;
    nav.pageSize = data.nav.pageSize;
    nav.currentPage = data.nav.currentPage;
    nav.pageCount = data.nav.pageCount;
    currentPage.value = data.nav.currentPage;

    updateNotificationsSidebarBadge(document, data.countNewMessages);
  } catch (error) {
    console.error(error);
    items.value = [];
    nav.totalCount = 0;
    nav.pageCount = 1;
  } finally {
    loading.value = false;
    syncFiltersVisibility();
    await syncSearchHighlight();
  }
}

function handlePageChange(page: number): void {
  currentPage.value = page;
  syncUrlToBrowser();
  void loadNotifications();
}
</script>
